import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";

import { buildInvalidError, type InvalidError } from "@thalamos/common";

import {
  MEANING_KEYWORD,
  type LanguageMap,
} from "../../assessments/authoring.js";
import {
  familyOf,
  parseInstrumentName,
  renderInstrumentName,
  type InstrumentNameUnbranded,
} from "../../assessments/name.js";
import type { PublishedInstrumentSchema } from "../../assessments/publishedSchema.js";
import { decorate, decorateStepwise } from "./decorate.js";
import type { Decorator } from "./decorator.js";
import {
  plainFormOf,
  type FormElement,
  type FormText,
  type Shown,
} from "./plainForm.js";

/**
 * Some versions a collector still supports, and the one set of decorators
 * serving them.
 *
 * A supported version has exactly one set, but how versions are grouped is the
 * collector's choice: one set per variant, so variants evolving at different
 * rates can be decorated differently, or one for a whole family whose variants
 * behave alike. A set never spans families, because a key only means one thing
 * within its family. Decorators common to several sets are shared by
 * including the same values in each.
 *
 * A version no set supports is shown through its plain form alone, so no
 * decorator code need be kept for it.
 */
export type SupportedVersions = {
  /**
   * Each version by its parts, without its revision, such as
   * `["sample", "core", "1.0"]`, serving every revision of it: a revision may
   * change a meaning's words, never what is asked, so one the collector has
   * not seen needs nothing new of its decorators, beyond words for any
   * language it adds that a rewording should give. `reportOnSupportedVersions`
   * catches a revision that breaks this, and notices the missing words.
   */
  readonly versions: readonly InstrumentNameUnbranded[];
  /** Applied in order, each seeing the form as those before it left it. */
  readonly decorators: readonly Decorator[];
};

/** A pinned version's form, and whether it was decorated. */
export type FormForVersion = {
  readonly form: FormElement[];
  /** `false` where the version is not supported, so the form is its plain one. */
  readonly supported: boolean;
};

/** The version a revision's name is of, rendered: every part but its last. */
const versionOf = (revision: readonly string[]): string =>
  renderInstrumentName(revision.slice(0, -1));

/** One published revision: its name, and its stored schema. */
export type PublishedRevision = {
  readonly name: InstrumentNameUnbranded;
  readonly schema: PublishedInstrumentSchema;
};

/**
 * The form for a pinned version: decorated where it is supported, and its
 * plain form otherwise, so a draft on a version no longer supported still
 * opens, losing its decorators but not its answers.
 *
 * Refused where the name is not an instrument's, the version has no plain
 * form, or the version is supported but its decorators do not apply:
 * `reportOnSupportedVersions` exists to catch the last before release.
 */
export const formFor = ({
  supported,
  name,
  schema,
  language,
  fallbackLanguage,
}: {
  supported: readonly SupportedVersions[];
  /** The pinned version's rendered name, such as `sample/core/1.0/r1`. */
  name: string;
  /** The pinned version's stored schema. */
  schema: PublishedInstrumentSchema;
  language: string;
  /** The language a banner falls back to, as for `decorate`. */
  fallbackLanguage: string;
}): Result<FormForVersion, InvalidError> =>
  plainFormOf({ schema, language }).andThen((form) => {
    const parsed = parseInstrumentName(name);

    if (!parsed.success) {
      return err(
        buildInvalidError({
          message: "The pinned name is not an instrument's",
          issues: [name],
        }),
      );
    }

    const version = versionOf(parsed.data);
    const support = supported.find((candidate) =>
      candidate.versions.some(
        (supported) => renderInstrumentName(supported) === version,
      ),
    );

    return support === undefined
      ? ok({ form, supported: false })
      : decorate({
          form,
          decorators: support.decorators,
          language,
          fallbackLanguage,
        }).map((decorated) => ({ form: decorated, supported: true }));
  });

/** Every language a stored schema's meanings give words in. */
const meaningLanguagesOf = (schema: PublishedInstrumentSchema): string[] => {
  const mapsOf = (
    meaning: { title: LanguageMap; description?: LanguageMap } | undefined,
  ): LanguageMap[] =>
    meaning === undefined
      ? []
      : [
          meaning.title,
          ...(meaning.description === undefined ? [] : [meaning.description]),
        ];
  const maps = Object.values(schema.properties).flatMap((group) => [
    ...mapsOf(group[MEANING_KEYWORD]),
    ...Object.values(group.properties).flatMap((item) => {
      const meaning = item[MEANING_KEYWORD];

      return [...mapsOf(meaning), ...(meaning?.answers ?? []).flatMap(mapsOf)];
    }),
  ]);

  return [...new Set(maps.flatMap((map) => Object.keys(map)))];
};

/** Every text a form shows, each named for a reader, such as `the title of "attendance"`. */
const textsIn = (form: readonly FormElement[]): [string, FormText][] => {
  const shown = (what: string, { title, description }: Shown) => [
    [`the title of ${what}`, title] as [string, FormText],
    ...(description === undefined
      ? []
      : [[`the description of ${what}`, description] as [string, FormText]]),
  ];

  return form.flatMap((element) => {
    if (element.kind === "group") {
      return shown(`group "${element.at.group}"`, element.presentation);
    }

    const { item } = element.at;
    const { control } = element.presentation;

    return [
      ...shown(`"${item}"`, element.presentation),
      ...(control.kind === "choice"
        ? control.options.flatMap((option) =>
            shown(
              `answer ${JSON.stringify(option.value)} of "${item}"`,
              option,
            ),
          )
        : []),
    ];
  });
};

/**
 * A notice for each language a reworded text shows the instrument's own words
 * in: the rewording gives nothing in that language, so the respondent sees
 * the canonical words instead, which is legal but probably not what the
 * rewording's author meant.
 */
const untranslatedIn = (
  built: readonly { language: string; form: readonly FormElement[] }[],
): string[] => {
  const texts = built.map(({ language, form }) => ({
    language,
    texts: textsIn(form),
  }));
  const reworded = new Set(
    texts.flatMap(({ texts: shown }) =>
      shown.flatMap(([what, text]) =>
        text.origin.kind === "collector" ? [what] : [],
      ),
    ),
  );

  return texts.flatMap(({ language, texts: shown }) =>
    shown.flatMap(([what, text]) =>
      reworded.has(what) && text.origin.kind === "canonical"
        ? [
            `the rewording of ${what} gives no "${language}", which its meaning has`,
          ]
        : [],
    ),
  );
};

/**
 * One revision built in one language: its plain form, that form decorated,
 * and the form as each decorator left it.
 */
type Built = {
  readonly language: string;
  readonly plain: readonly FormElement[];
  readonly decorated: readonly FormElement[];
  readonly steps: readonly (readonly FormElement[])[];
};

/**
 * A form as a string two forms can be compared by: a check or rule by its
 * identity, and an object by its entries whatever their order, so how a
 * view is written back cannot make an unchanged form look changed.
 */
const comparableWith =
  (identities: Map<unknown, number>) =>
  (form: readonly FormElement[]): string => {
    const identityOf = (value: unknown): number => {
      const known = identities.get(value);

      if (known !== undefined) {
        return known;
      }

      identities.set(value, identities.size);

      return identities.size - 1;
    };

    return JSON.stringify(form, (_key, value: unknown) => {
      if (typeof value === "function") {
        return `fn#${String(identityOf(value))}`;
      }

      return value instanceof Object && !Array.isArray(value)
        ? Object.fromEntries(
            Object.entries(value).toSorted(([left], [right]) =>
              left < right ? -1 : 1,
            ),
          )
        : value;
    });
  };

/**
 * A notice for each decorator that changes nothing in any form built, such
 * as a rewording giving only a misspelt language, or one keyed by a module no
 * supported version is of. Named by its position, counting from 1, since a
 * decorator has no name. Nothing is noticed where no build succeeded, whose
 * refusals are reported already.
 */
const idleIn = (
  versions: readonly string[],
  decorators: readonly Decorator[],
  builds: readonly Built[],
): string[] => {
  const comparable = comparableWith(new Map());
  const changes = builds.map(({ plain, steps }) => {
    const shown = [plain, ...steps].map(comparable);

    return steps.map((_step, index) => shown[index] !== shown[index + 1]);
  });

  return builds.length === 0
    ? []
    : decorators.flatMap((_decorator, index) =>
        changes.some((changed) => changed[index] === true)
          ? []
          : [
              `${versions.join(", ")}: decorator ${String(index + 1)} changes nothing in any revision or language built`,
            ],
      );
};

/**
 * A notice for each reworded text whose canonical words differ between the
 * revisions of one version built in a language the rewording gives: it was
 * written against one revision's words, so may keep a sense a later one
 * corrected, and every decorated form shows the same rewording. A revision
 * showing a key where it has no meaning in a language is no correction.
 */
const correctedBeneath = (
  version: string,
  revisions: readonly { revision: string; built: readonly Built[] }[],
): string[] => {
  const languages = [
    ...new Set(
      revisions.flatMap(({ built }) => built.map(({ language }) => language)),
    ),
  ];

  return languages.flatMap((language) => {
    const inLanguage = revisions.flatMap(({ revision, built }) =>
      built
        .filter((candidate) => candidate.language === language)
        .map(({ plain, decorated }) => ({
          revision,
          canonical: new Map(textsIn(plain)),
          decorated: textsIn(decorated),
        })),
    );
    const reworded = [
      ...new Set(
        inLanguage.flatMap(({ decorated }) =>
          decorated.flatMap(([what, text]) =>
            text.origin.kind === "collector" ? [what] : [],
          ),
        ),
      ),
    ];

    return reworded.flatMap((what) => {
      // A text some revisions lack is a change too, so a missing one compares
      // as `undefined`.
      const worded = inLanguage.flatMap(({ revision, canonical }) => {
        const text = canonical.get(what);

        return text?.origin.kind === "identifier"
          ? []
          : [{ revision, words: JSON.stringify(text) }];
      });
      const named = worded.map(({ revision }) => revision);

      return new Set(worded.map(({ words }) => words)).size > 1
        ? [
            `${version}: the rewording of ${what} stands in for "${language}" words that differ between ${named.slice(0, -1).join(", ")} and ${named.slice(-1).join("")}`,
          ]
        : [];
    });
  });
};

/** What the decorators would do to every revision of every supported version. */
export type SupportReport = {
  /** Anything wrong with the declared support or its decorators: must be empty before release. */
  readonly refused: readonly string[];
  /**
   * Anything legal but likely a mistake, for a reader to decide on: only what
   * the built forms show, since a decorator is a policy the report cannot see
   * inside, so a key no supported version asks goes unnoticed where its
   * decorator changes something else.
   */
  readonly noticed: readonly string[];
};

/**
 * One set's report: every published revision of each version it supports,
 * decorated, as its own form would be, in the fallback language and in every
 * language its meanings give, since a text decorator's result can differ by
 * language.
 */
const reportOnSet = (
  support: SupportedVersions,
  published: readonly PublishedRevision[],
  fallbackLanguage: string,
): SupportReport => {
  const versions = support.versions.map(renderInstrumentName);
  const families = new Set(support.versions.map(familyOf));
  const acrossFamilies =
    families.size > 1
      ? [
          `${versions.map((version) => `"${version}"`).join(", ")} span more than one family`,
        ]
      : [];
  const unpublished = versions
    .filter(
      (version) =>
        !published.some((revision) => versionOf(revision.name) === version),
    )
    .map(
      (version) =>
        `"${version}" is supported, but no revision of it is published`,
    );
  const revisions = published
    .filter((revision) => versions.includes(versionOf(revision.name)))
    .map(({ name, schema }) => {
      const built = [
        ...new Set([fallbackLanguage, ...meaningLanguagesOf(schema)]),
      ].map((language) => ({
        language,
        form: plainFormOf({ schema, language }).andThen((plain) =>
          decorateStepwise({
            form: plain,
            decorators: support.decorators,
            language,
            fallbackLanguage,
          }).map(({ form: decorated, steps }) => ({
            plain,
            decorated,
            steps,
          })),
        ),
      }));

      return { name, built };
    });
  const succeeded = revisions.map(({ name, built }) => ({
    name,
    built: built.flatMap(({ language, form }): Built[] =>
      form.match(
        (forms) => [{ language, ...forms }],
        () => [],
      ),
    ),
  }));

  // A structural fault refuses every language alike, so is reported once.
  const refused = revisions.flatMap(({ name, built }) =>
    [
      ...new Set(
        built.flatMap(({ form }) =>
          form.match(
            (): string[] => [],
            (error) => error.issues,
          ),
        ),
      ),
    ].map((issue) => `${renderInstrumentName(name)}: ${issue}`),
  );
  const untranslated = succeeded.flatMap(({ name, built }) =>
    untranslatedIn(
      built.map(({ language, decorated }) => ({ language, form: decorated })),
    ).map((gap) => `${renderInstrumentName(name)}: ${gap}`),
  );
  const corrected = versions.flatMap((version) =>
    correctedBeneath(
      version,
      succeeded
        .filter(({ name }) => versionOf(name) === version)
        .map(({ name, built }) => ({
          revision: renderInstrumentName(name.slice(-1)),
          built,
        })),
    ),
  );

  const idle = idleIn(
    versions,
    support.decorators,
    succeeded.flatMap(({ built }) => built),
  );

  return {
    refused: [...acrossFamilies, ...unpublished, ...refused],
    noticed: [...new Set(untranslated), ...corrected, ...idle],
  };
};

/**
 * Every problem the decorators would cause across the supported versions, for
 * a test to run before release, so failing loudly at form build is never an
 * assessor's problem.
 */
export const reportOnSupportedVersions = ({
  supported,
  published,
  fallbackLanguage,
}: {
  supported: readonly SupportedVersions[];
  /** Every published revision, for the supported versions to be found among. */
  published: readonly PublishedRevision[];
  /** The language the collector's banners fall back to, as for `decorate`. */
  fallbackLanguage: string;
}): SupportReport => {
  const versions = supported.flatMap((support) =>
    support.versions.map(renderInstrumentName),
  );
  // Two sets for one version would leave which serves it to whichever is
  // found first.
  const repeated = [...new Set(versions)]
    .filter(
      (version) => versions.indexOf(version) !== versions.lastIndexOf(version),
    )
    .map((version) => `"${version}" is supported more than once`);
  const reports = supported.map((support) =>
    reportOnSet(support, published, fallbackLanguage),
  );

  return {
    refused: [...repeated, ...reports.flatMap(({ refused }) => refused)],
    noticed: reports.flatMap(({ noticed }) => noticed),
  };
};
